// 결제 도메인 공통 타입/스키마 (인앱 PG: 포트원 V2 + KPN 빌링키).
// 사용자앱(결제수단 관리/체크아웃), 관리자(결제상태), API 가 공유한다.
// 설계: docs/payment.md. 카드 원본 무저장 - billing_key/customer_uid/마스킹만 다룬다.

import { z } from "zod";

// 결제수단 유형. 주문 유형별 선택지: 포장=[inapp_direct, onsite] / 배달=[inapp_direct, sms_request].
//  - inapp_direct(일반 카드결제)는 논리값이다. DB orders.payment_method_type 컬럼엔 'inapp' 로 저장하고
//    별도 플래그(orders.direct_charge=1)로 구분한다(CHECK 제약 재생성을 피하기 위함, 설계: docs/payment.md).
//    repository 경계(createOrder 쓰기 / orderRowToDto 읽기)에서만 물리값<->논리값을 번역한다.
//  - inapp(빌링키 재사용)은 폐지됐다: KPN 이 "정기결제(구독)가 아니다"라며 빌링키 MID 발급을 반려해
//    카드 등록/재사용 기능을 접었다. 과거 주문 조회/환불 호환을 위해 타입만 남긴다(신규 선택 불가).
export const PAYMENT_METHOD_TYPE = {
  ONSITE: "onsite", // 현장 결제(포장 수령 시). 실중량 정산 가능.
  INAPP: "inapp", // (폐지) 빌링키 재사용 결제. 과거 주문 호환용.
  INAPP_DIRECT: "inapp_direct", // 일반 카드결제(결제창 일회성, 주문 시 표시 금액 즉시결제).
  SMS_REQUEST: "sms_request", // SMS 일회용 결제요청(배달). 실중량 정산 가능.
} as const;
export type PaymentMethodType = (typeof PAYMENT_METHOD_TYPE)[keyof typeof PAYMENT_METHOD_TYPE];

export const PAYMENT_METHOD_TYPE_LABEL_KO: Record<PaymentMethodType, string> = {
  onsite: "현장 결제",
  inapp: "카드 결제",
  inapp_direct: "일반 카드결제",
  sms_request: "SMS 결제요청",
};

// 결제 상태 머신. unpaid -> charging(인앱 청구 점유) -> paid | failed -> refunded | refund_failed.
//  - charging: 청구 진행 중 단일 진입 락(이중청구 차단). 잔류 시 재시도/Cron 스윕으로 수렴.
//  - refund_failed: 환불 API 실패(재시도/알림 대상).
export const PAYMENT_STATUS = {
  UNPAID: "unpaid",
  CHARGING: "charging",
  PAID: "paid",
  FAILED: "failed",
  REFUNDED: "refunded",
  REFUND_FAILED: "refund_failed",
} as const;
export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

export const PAYMENT_STATUS_LABEL_KO: Record<PaymentStatus, string> = {
  unpaid: "미결제",
  charging: "결제 처리중",
  paid: "결제완료",
  failed: "결제실패",
  refunded: "환불완료",
  refund_failed: "환불실패",
};

// 인앱 청구 재시도 대상 상태(조건부 락이 charging 으로 점유 가능한 출발 상태).
export const PAYMENT_CHARGE_ELIGIBLE_STATUSES: ReadonlyArray<PaymentStatus> = [
  PAYMENT_STATUS.UNPAID,
  PAYMENT_STATUS.FAILED,
];

// 실중량 정산이 가능한(= 나중에 최종 금액을 확정해 받는) 결제수단. 주문 유형별로 하나씩이다.
//  - 포장: 수령 시 현장 결제 / 배달: 준비완료 후 SMS 결제요청.
export function settlementPaymentMethodType(
  orderType: "pickup" | "delivery",
): PaymentMethodType {
  return orderType === "delivery" ? PAYMENT_METHOD_TYPE.SMS_REQUEST : PAYMENT_METHOD_TYPE.ONSITE;
}

// 주문 유형별 허용 결제수단(서버/클라 공통 검증 근거).
//  - 포장(pickup): 일반 카드결제 + 현장 결제. 배달(delivery): 일반 카드결제 + SMS 결제요청.
//  - 빌링키(inapp)는 더 이상 신규 선택지가 아니다. KPN 정기결제 MID 심사 대상이 아니어서 기능을 접었고,
//    과거 주문 조회/환불 호환을 위해 타입만 남겨둔다.
//  - hasWeightAdjustableItem(실중량 관리 대상품 포함)이면 주문 시점에 금액을 확정할 수 없으므로
//    카드 즉시결제를 막고 정산 가능한 수단(현장/SMS)만 남긴다.
export function allowedPaymentMethodTypes(
  orderType: "pickup" | "delivery",
  opts?: { hasWeightAdjustableItem?: boolean },
): PaymentMethodType[] {
  const settlement = settlementPaymentMethodType(orderType);
  if (opts?.hasWeightAdjustableItem) {
    return [settlement];
  }
  return [PAYMENT_METHOD_TYPE.INAPP_DIRECT, settlement];
}

// 응답 DTO: 저장된 결제수단(빌링키 카드). billing_key 자체는 응답에 절대 포함하지 않는다.
export interface PaymentMethodDto {
  id: string;
  cardBrand: string | null;
  cardLast4: string | null;
  label: string | null;
  isDefault: boolean;
  createdAt: string;
}

// 빌링키 저장 요청(클라가 포트원 SDK 로 발급한 billingKey 를 서버에 전달).
//  - 카드 메타(brand/last4)는 서버가 포트원 API 로 조회해 채우므로 받지 않는다.
export const savePaymentMethodInputSchema = z.object({
  billingKey: z.string().trim().min(1).max(300),
  label: z.string().trim().max(30).nullable().optional(),
  // 첫 카드거나 사용자가 지정하면 기본 카드로.
  setDefault: z.boolean().optional(),
});
export type SavePaymentMethodInput = z.infer<typeof savePaymentMethodInputSchema>;

// 주문당 안정 결제 식별자. 재시도에도 불변 -> 포트원이 동일 paymentId 로 중복청구를 막는다.
//  - 제약 두 가지를 동시에 만족해야 한다:
//    (1) 포트원 SDK requestPayment: 영문/숫자만 허용(ALLOWED_CHARACTERS, otherCharacters="").
//    (2) KPN 매입(MxIssueNO): 최대 32 byte.
//    orderId(UUID)에서 영문/숫자만 남기면 32자(하이픈 제거)라 그대로 32 byte 한도에 맞는다. 접두사는 두지 않는다.
//    혹시 더 긴 id 가 와도 32자로 잘라 한도를 보장한다(UUID 32 hex 는 그대로 고유).
//  - 서버 REST(청구/취소/조회)와 클라 SDK 가 반드시 같은 값을 써야 하므로 양쪽 모두 이 함수를 사용한다.
export function stablePgPaymentId(orderId: string): string {
  return orderId.replace(/[^a-zA-Z0-9]/g, "").slice(0, 32);
}

// 사용자별 포트원 고객 식별자(customerId). 빌링키 발급(클라 SDK)과 저장(서버)이 같은 값을 써야 일관된다.
export function portoneCustomerId(userId: string): string {
  return `cust_${userId}`;
}
